import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import carrierService from '../../services/carrierService';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Finance = () => {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCarriers();
  }, []);

  const fetchCarriers = async () => {
    try {
      setLoading(true);
      const response = await carrierService.getCarriersForFinance();
      if (response?.success) {
        setCarriers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching carriers for finance:', error);
      toast.error('Taşıyıcı firmaları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredCarriers = carriers.filter(carrier =>
    carrier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (carrier.contact_person && carrier.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleCarrierClick = (carrierId) => {
    navigate(`/finance/carrier/${carrierId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CurrencyDollarIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Finans Yönetimi</h1>
              <p className="text-gray-600">Taşıyıcı firma komisyon ödemeleri ve takibi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="max-w-md">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
            Taşıyıcı Firma Ara
          </label>
          <input
            type="text"
            id="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Firma adı veya yetkili kişi..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Carriers List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Taşıyıcı Firmalar</h2>
          <p className="text-sm text-gray-600">{filteredCarriers.length} firma listeleniyor</p>
        </div>

        {filteredCarriers.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'Arama kriterlerine uygun firma bulunamadı' : 'Henüz taşıyıcı firma bulunmuyor'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCarriers.map((carrier) => (
              <div
                key={carrier.id}
                onClick={() => handleCarrierClick(carrier.id)}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{carrier.name}</h3>
                        {carrier.contact_person && (
                          <p className="text-sm text-gray-600">Yetkili: {carrier.contact_person}</p>
                        )}
                        {carrier.phone && (
                          <p className="text-sm text-gray-600">Tel: {carrier.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="mt-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <BanknotesIcon className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="text-xs text-gray-500">Toplam Transfer</p>
                          <p className="text-sm font-medium">{carrier.total_transfers || 0}</p>
                        </div>
                      </div>

                      {/* Para birimi bazında gösterim */}
                      {carrier.by_currency && Object.keys(carrier.by_currency).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {Object.entries(carrier.by_currency).map(([currency, amounts]) => (
                            <div key={currency} className="bg-gray-50 rounded-lg p-3">
                              <div className="text-xs font-medium text-gray-700 mb-2">{currency}</div>
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500">Toplam:</span>
                                  <span className="text-sm font-medium">{formatCurrency(amounts.total, currency)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-green-600">Ödenen:</span>
                                  <span className="text-sm font-medium text-green-600">{formatCurrency(amounts.paid, currency)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-orange-600">Bekleyen:</span>
                                  <span className="text-sm font-medium text-orange-600">{formatCurrency(amounts.pending, currency)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500">Henüz komisyon bilgisi bulunmuyor</div>
                      )}
                    </div>
                  </div>

                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Finance;
